/**
 * SpawnPoint - Defines player and enemy spawn locations
 * Can serve as respawn points, checkpoints, and level entry points
 */

import * as THREE from 'three';
import { Entity } from './Entity';
import type { Vector2 } from '../../types/game';

export type SpawnPointType = 'player' | 'enemy' | 'checkpoint';

export interface SpawnPointConfig {
  id: string;
  position: Vector2;
  type: SpawnPointType;
  metadata?: {
    facingDirection?: 'left' | 'right';
    isDefault?: boolean;
    checkpointName?: string;
    respawnDelay?: number; // seconds
  };
}

export class SpawnPoint extends Entity {
  // Spawn point properties
  public spawnId: string;
  public spawnType: SpawnPointType;
  public facingDirection: 'left' | 'right';
  public isDefault: boolean;
  public checkpointName: string;
  public respawnDelay: number;

  // Checkpoint state (for player checkpoints)
  private isActivated: boolean = false;
  private activationTime: number = 0;

  // Visual rendering
  private mesh: THREE.Group | null = null;

  constructor(config: SpawnPointConfig) {
    // Checkpoints are larger for better visibility
    const size = config.type === 'checkpoint' ? { width: 50, height: 50 } : { width: 30, height: 30 };
    super(config.id, 'spawn_point', config.position, size);

    this.spawnId = config.id;
    this.spawnType = config.type;
    this.facingDirection = config.metadata?.facingDirection || 'right';
    this.isDefault = config.metadata?.isDefault || false;
    this.checkpointName = config.metadata?.checkpointName || config.id;
    this.respawnDelay = config.metadata?.respawnDelay || 0;

    // Spawn points don't have health
    this.health = -1;
    this.maxHealth = -1;

    // Create visual mesh (debug visualization)
    this.createMesh();
  }

  /**
   * Create Three.js mesh for the spawn point (debug visualization)
   */
  private createMesh(): void {
    this.mesh = new THREE.Group();

    // Different colors for different spawn types
    let color: number;
    switch (this.spawnType) {
      case 'player':
        color = 0x00ff00; // Green for player spawns
        break;
      case 'checkpoint':
        color = 0xffff00; // Yellow for checkpoints
        break;
      case 'enemy':
        color = 0xff0000; // Red for enemy spawns
        break;
      default:
        color = 0xffffff;
    }

    // Create checkpoint marker - larger and more visible
    if (this.spawnType === 'checkpoint') {
      // Outer glow circle
      const glowGeometry = new THREE.CircleGeometry(this.size.width / 2 + 5, 32);
      const glowMaterial = new THREE.MeshBasicMaterial({
        color: color,
        opacity: 0.3,
        transparent: true,
      });
      const glowCircle = new THREE.Mesh(glowGeometry, glowMaterial);
      glowCircle.position.set(0, 0, -1);
      this.mesh.add(glowCircle);

      // Main circle
      const circleGeometry = new THREE.CircleGeometry(this.size.width / 2, 32);
      const circleMaterial = new THREE.MeshBasicMaterial({
        color: color,
        opacity: 0.6,
        transparent: true,
      });
      const circle = new THREE.Mesh(circleGeometry, circleMaterial);
      this.mesh.add(circle);

      // Border ring
      const ringGeometry = new THREE.RingGeometry(this.size.width / 2 - 3, this.size.width / 2, 32);
      const ringMaterial = new THREE.MeshBasicMaterial({
        color: 0xffffff,
        opacity: 0.8,
        transparent: true,
      });
      const ring = new THREE.Mesh(ringGeometry, ringMaterial);
      ring.position.set(0, 0, 1);
      this.mesh.add(ring);
    } else {
      // Simple marker for player/enemy spawns
      const markerGeometry = new THREE.PlaneGeometry(this.size.width, this.size.height);
      const markerMaterial = new THREE.MeshBasicMaterial({
        color: color,
        opacity: 0.5,
        transparent: true,
      });
      const marker = new THREE.Mesh(markerGeometry, markerMaterial);
      this.mesh.add(marker);
    }

    this.mesh.position.set(this.position.x, this.position.y, 0);
  }

  /**
   * Update spawn point state
   */
  public update(deltaTime: number): void {
    if (this.destroyed || !this.active) return;

    // Update mesh position
    if (this.mesh) {
      this.mesh.position.set(this.position.x, this.position.y, 0);

      // Pulse effect for activated checkpoints
      if (this.spawnType === 'checkpoint' && this.isActivated) {
        const pulse = Math.sin(Date.now() / 300) * 0.2 + 1;
        this.mesh.scale.set(pulse, pulse, 1);

        // Update color to show activation
        this.mesh.children.forEach(child => {
          if (child instanceof THREE.Mesh || child instanceof THREE.Line) {
            const material = child.material as THREE.MeshBasicMaterial | THREE.LineBasicMaterial;
            material.color.setHex(0x00ff00); // Green when activated
          }
        });
      }
    }
  }

  /**
   * Render spawn point (debug)
   */
  public render(): void {
    // Mesh is automatically rendered by Three.js scene
  }

  /**
   * Activate checkpoint (for player checkpoints)
   */
  public activate(): void {
    if (this.spawnType !== 'checkpoint' && this.spawnType !== 'player') {
      return;
    }

    this.isActivated = true;
    this.activationTime = Date.now();
    console.log(`✅ Checkpoint activated: ${this.checkpointName}`);
  }

  /**
   * Deactivate checkpoint
   */
  public deactivate(): void {
    this.isActivated = false;
    console.log(`❌ Checkpoint deactivated: ${this.checkpointName}`);
  }

  /**
   * Get spawn position and direction
   */
  public getSpawnInfo(): {
    position: Vector2;
    facingDirection: 'left' | 'right';
  } {
    return {
      position: { ...this.position },
      facingDirection: this.facingDirection,
    };
  }

  // Getters
  public getIsActivated(): boolean {
    return this.isActivated;
  }

  public getActivationTime(): number {
    return this.activationTime;
  }

  public getIsDefault(): boolean {
    return this.isDefault;
  }

  public getSpawnType(): SpawnPointType {
    return this.spawnType;
  }

  public getCheckpointName(): string {
    return this.checkpointName;
  }

  public getMesh(): THREE.Group | null {
    return this.mesh;
  }

  /**
   * Get spawn point info for UI/debugging
   */
  public getInfo(): {
    id: string;
    type: SpawnPointType;
    name: string;
    activated: boolean;
    isDefault: boolean;
    position: Vector2;
  } {
    return {
      id: this.spawnId,
      type: this.spawnType,
      name: this.checkpointName,
      activated: this.isActivated,
      isDefault: this.isDefault,
      position: { ...this.position },
    };
  }
}
